import React from 'react'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const calcMargin = (cost, sell) => {
  if (!sell) return '\u2014'
  return (((sell - cost) / sell) * 100).toFixed(1) + '%'
}

const marginColor = (cost, sell) => {
  if (!sell) return 'var(--ink-3)'
  const m = ((sell - cost) / sell) * 100
  if (m >= 20) return 'var(--ok)'
  if (m >= 10) return 'var(--warn)'
  return 'var(--risk)'
}

const covColor = (pct) => {
  if (pct >= 90) return 'var(--brand, var(--primary))'
  if (pct >= 50) return 'var(--warn)'
  return 'var(--risk)'
}

const SectionBreakdown = ({ bySection, byBuilding, selectedBuilding }) => {
  const [viewMode, setViewMode] = React.useState('section')

  const data = React.useMemo(() => {
    if (selectedBuilding && byBuilding && byBuilding[selectedBuilding]) {
      const bld = byBuilding[selectedBuilding]
      return [{ section: selectedBuilding + ' (All Sections)', items: bld.items || 0, totalCost: bld.totalCost || 0, totalSell: bld.totalSell || 0, covered: bld.covered || 0 }]
    }
    if (!bySection) return []
    return Object.entries(bySection).map(([name, vals]) => ({
      section: name,
      items: vals.items || 0,
      totalCost: vals.totalCost || 0,
      totalSell: vals.totalSell || 0,
      covered: vals.covered || 0
    })).sort((a, b) => a.section.localeCompare(b.section))
  }, [bySection, byBuilding, selectedBuilding])

  const totals = React.useMemo(() => {
    return data.reduce((acc, row) => ({
      items: acc.items + row.items,
      totalCost: acc.totalCost + row.totalCost,
      totalSell: acc.totalSell + row.totalSell,
      covered: acc.covered + row.covered
    }), { items: 0, totalCost: 0, totalSell: 0, covered: 0 })
  }, [data])

  const totalCovPct = totals.items > 0 ? Math.round((totals.covered / totals.items) * 100) : 0

  if (!data.length) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No section data available.</div>
  }

  return (
    <div>
      {/* Header with view toggle */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Section Breakdown</h4>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
          {['section', 'building', 'trade'].map(mode => (
            <button
              key={mode}
              className={`hmi-btn hmi-btn-sm ${viewMode === mode ? 'hmi-btn-outline' : 'hmi-btn-ghost'}`}
              style={{ height: 26, fontSize: 12, borderRadius: 6, padding: '0 10px', fontWeight: viewMode === mode ? 600 : 500 }}
              onClick={() => setViewMode(mode)}
            >
              By {mode}
            </button>
          ))}
        </div>
      </div>

      <table className="hmi-table">
        <thead>
          <tr>
            <th>Section</th>
            <th style={{ textAlign: 'right', width: 60 }}>Items</th>
            <th style={{ textAlign: 'right', width: 100 }}>Cost</th>
            <th style={{ textAlign: 'right', width: 100 }}>Sell</th>
            <th style={{ textAlign: 'right', width: 70 }}>Margin</th>
            <th style={{ textAlign: 'right', width: 120 }}>Coverage</th>
            <th style={{ width: 24 }}></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const covPct = row.items > 0 ? Math.round((row.covered / row.items) * 100) : 0
            return (
              <tr key={row.section}>
                <td>{row.section}</td>
                <td className="num">{row.items}</td>
                <td className="num">{fmt(row.totalCost)}</td>
                <td className="num">{fmt(row.totalSell)}</td>
                <td className="num" style={{ color: marginColor(row.totalCost, row.totalSell) }}>{calcMargin(row.totalCost, row.totalSell)}</td>
                <td style={{ textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 12, color: 'var(--ink-3)' }}>{covPct}%</span>
                    <div className="cov"><div className="bar"><i style={{ width: `${covPct}%`, background: covColor(covPct) }} /></div></div>
                  </div>
                </td>
                <td style={{ color: 'var(--ink-4)', fontSize: 14 }}>{'\u203A'}</td>
              </tr>
            )
          })}
          <tr style={{ background: 'var(--bg-panel-alt)', fontWeight: 600 }}>
            <td><strong>Grand total</strong></td>
            <td className="num"><strong>{totals.items}</strong></td>
            <td className="num"><strong>{fmt(totals.totalCost)}</strong></td>
            <td className="num"><strong>{fmt(totals.totalSell)}</strong></td>
            <td className="num" style={{ color: marginColor(totals.totalCost, totals.totalSell) }}><strong>{calcMargin(totals.totalCost, totals.totalSell)}</strong></td>
            <td style={{ textAlign: 'right' }}>
              <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 12 }}>{totalCovPct}%</span>
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default SectionBreakdown
